
import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  useSidebar,
} from "@/components/ui/sidebar";
import { CategoryItem } from "./category/CategoryItem";
import { CategoryDefinition } from "@/types/habit";
import { AllCategoriesButton } from "./category/AllCategoriesButton";
import { AddCategoryPopover } from "./category/AddCategoryPopover";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "./ui/button";
import { PanelLeft } from "lucide-react";
import { Sheet, SheetContent } from "./ui/sheet";

interface CategorySidebarProps {
  selectedCategory: string | null;
  setSelectedCategory: (category: string | null) => void;
  handleRemoveCategory: (category: string) => void;
  handleAddNewCategoryToSystem: (category: string, iconSrc?: string) => void;
  handleUpdateCategory: (categoryName: string, newName: string, newIconSrc?: string) => void;
  categories: CategoryDefinition[];
}

export const CategorySidebar = ({
  selectedCategory,
  setSelectedCategory,
  handleRemoveCategory,
  handleAddNewCategoryToSystem,
  handleUpdateCategory,
  categories,
}: CategorySidebarProps) => {
  const { state, openMobile, setOpenMobile } = useSidebar();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const toggleMobileSidebar = () => {
    setOpenMobile(!openMobile);
    setOpen(!open);
  };

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const categoryContent = (
    <div className="flex flex-col h-full">
      <div className="flex items-center px-2 group">
        <div className="flex items-center gap-2 w-full">
          {isVisible && (
            <>
              <AllCategoriesButton 
                isSelected={!selectedCategory} 
                onClick={() => {
                  setSelectedCategory(null);
                  if (isMobile) setOpen(false);
                }} 
              />
              
              <AddCategoryPopover onAddCategory={handleAddNewCategoryToSystem} />
            </>
          )}

          {!isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleVisibility}
              className={cn(
                "h-8 w-8",
                !isVisible && "ml-2"
              )}
            >
              <PanelLeft className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      
      {isVisible && (
        <div className="flex-1 overflow-y-auto mt-2">
          <ul className="list-none m-0 p-0">
            {categories.map((category) => (
              <li key={category.name} className="mb-1">
                <CategoryItem
                  category={category}
                  isSelected={selectedCategory === category.name}
                  onSelect={() => {
                    if (selectedCategory === category.name) {
                      setSelectedCategory(null);
                    } else {
                      setSelectedCategory(category.name);
                    }
                    if (isMobile) setOpen(false);
                  }}
                  onUpdate={handleUpdateCategory}
                  onDelete={handleRemoveCategory}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <>
      {isMobile ? (
        <>
          <Button
            variant="outline"
            size="icon"
            className="fixed bottom-4 left-4 z-50 rounded-full shadow-lg bg-white"
            onClick={toggleMobileSidebar}
          >
            <PanelLeft className="h-6 w-6" />
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent side="left" className="p-4 w-[280px] bg-white">
              {categoryContent}
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <div 
          className={cn(
            "transition-all duration-200",
            isVisible ? "w-[280px]" : "w-[48px]"
          )}
          data-tour="category-sidebar"
        >
          <Sidebar className="border-none mt-[95px]">
            <SidebarContent className="pt-1">
              <SidebarGroup>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {categoryContent}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            </SidebarContent>
          </Sidebar>
        </div>
      )}
    </>
  );
};
